import {
    useCallback,
    useContext,
    useEffect,
    useState
} from "react";
import {
    TextInput as NativeTextInput,
    useWindowDimensions,
    TouchableOpacity,
    ScrollView,
    View
} from "react-native";
import type {
    FinanceCategory,
    FinanceItem
} from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitDialog,
    NCoreUIKitToast,
    NCoreUIKitTheme,
    DateTimePicker,
    PageContainer,
    SelectBox,
    StateCard,
    TextInput,
    Button,
    Modal,
    Text
} from "ncore-ui-kit";
import type {
    MemberItem
} from "../members/type";
import {
    ChevronRightIcon,
    ChevronLeftIcon,
    RefreshCcwIcon,
    BanknoteIcon,
    SearchIcon,
    PencilIcon,
    TrashIcon
} from "lucide-react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import {
    AuthContext
} from "../../contexts/AuthContext";
const Finance = () => {
    const {
        radiuses,
        spaces,
        colors
    } = NCoreUIKitTheme.useContext();

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const {
        activeOrganizationId,
        user
    } = useContext(AuthContext);

    const {
        width
    } = useWindowDimensions();
    const isMobile = width < 768;

    const [
        finances,
        setFinances
    ] = useState<FinanceItem[]>([]);

    const [
        isRefreshing,
        setIsRefreshing
    ] = useState<boolean>(false);

    const [
        categories,
        setCategories
    ] = useState<FinanceCategory[]>([]);

    const [
        members,
        setMembers
    ] = useState<MemberItem[]>([]);

    const [
        editId,
        setEditId
    ] = useState<string | null>(null);

    const [
        search,
        setSearch
    ] = useState("");

    const [
        debouncedSearch,
        setDebouncedSearch
    ] = useState("");

    const [
        totalBalance,
        setTotalBalance
    ] = useState<number>(0);

    const [
        filteredTotal,
        setFilteredTotal
    ] = useState<number>(0);

    const [
        totalPages,
        setTotalPages
    ] = useState<number>(1);

    const [
        page,
        setPage
    ] = useState<number>(1);

    const [
        jumpPage,
        setJumpPage
    ] = useState<string>("");

    const [
        startDate,
        setStartDate
    ] = useState("");

    const [
        endDate,
        setEndDate
    ] = useState("");

    const [
        selectedCategoryId,
        setSelectedCategoryId
    ] = useState<string>("");

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setDebouncedSearch(search);
            setPage(1);
        }, 500);
        return () => clearTimeout(timeoutId);
    }, [search]);

    const [
        modalVisible,
        setModalVisible
    ] = useState(false);

    const [
        isSubmitting,
        setIsSubmitting
    ] = useState(false);

    const [
        modalType,
        setModalType
    ] = useState<"income" | "expense">("income");

    const [
        formData,
        setFormData
    ] = useState({
        member_ids: [] as string[],
        category_id: "",
        amount: "",
        title: ""
    });

    const fetchCategories = useCallback(async () => {
        if (user?.role === "root" && !activeOrganizationId) {
            setCategories([]);
            return;
        }

        try {
            const token = await AsyncStorage.getItem("authToken");
            const query = activeOrganizationId ? `?organization_id=${activeOrganizationId}` : "";
            const response = await fetch(`${process.env.API_URL}/finance-categories${query}`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            const result = await response.json();
            if (result.status === "success") {
                setCategories(result.data);
            }
        } catch (error) {
            console.error(localize("finance-toast-unknown-error"), error);
        }
    }, [
        user?.role,
        activeOrganizationId
    ]);

    const fetchMembers = useCallback(async () => {
        if (user?.role === "root" && !activeOrganizationId) {
            setMembers([]);
            return;
        }

        try {
            const token = await AsyncStorage.getItem("authToken");
            let query = "?limit=1000";
            if (activeOrganizationId) {
                query += `&organization_id=${activeOrganizationId}`;
            }

            const response = await fetch(`${process.env.API_URL}/members${query}`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            const result = await response.json();
            if (result.status === "success") {
                setMembers(result.data);
            }
        } catch (error) {
            console.error(localize("finance-toast-unknown-error"), error);
        }
    }, [
        user?.role,
        activeOrganizationId
    ]);

    const fetchFinances = useCallback(async () => {
        if (user?.role === "root" && !activeOrganizationId) {
            setFinances([]);
            setTotalBalance(0);
            setFilteredTotal(0);
            setTotalPages(1);
            return;
        }

        try {
            const token = await AsyncStorage.getItem("authToken");
            let query = `?page=${page}&limit=10&`;
            if (activeOrganizationId) query += `organization_id=${activeOrganizationId}&`;
            if (debouncedSearch) query += `search=${encodeURIComponent(debouncedSearch)}&`;
            if (selectedCategoryId) query += `category_id=${selectedCategoryId}&`;
            if (startDate) query += `startDate=${startDate}&`;
            if (endDate) query += `endDate=${endDate}&`;

            const response = await fetch(`${process.env.API_URL}/finances${query}`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            const result = await response.json();
            if (result.status === "success") {
                setFinances(result.data);
                if (result.meta) {
                    setTotalBalance(result.meta.totalBalance || 0);
                    setFilteredTotal(result.meta.filteredTotal || 0);
                    setTotalPages(result.meta.totalPages || 1);
                }
            }
        } catch (error) {
            console.error(localize("finance-toast-unknown-error"), error);
        }
    }, [
        user?.role,
        activeOrganizationId,
        debouncedSearch,
        selectedCategoryId,
        startDate,
        endDate,
        page
    ]);

    useEffect(() => {
        fetchCategories();
    }, [fetchCategories]);

    useEffect(() => {
        fetchMembers();
    }, [fetchMembers]);

    useEffect(() => {
        fetchFinances();
    }, [fetchFinances]);

    const handleSave = async () => {
        if (!formData.title || !formData.amount || (user?.role === "root" && !formData.category_id)) {
            alert(localize("finance-alert-required"));

            return;
        }

        if (user?.role === "root" && !activeOrganizationId) {
            alert(localize("finance-alert-org"));

            return;
        }

        if (isSubmitting) {
            return;
        }

        setIsSubmitting(true);

        try {
            const token = await AsyncStorage.getItem("authToken");
            const url = editId
                ? `${process.env.API_URL}/finances/${editId}`
                : `${process.env.API_URL}/finances`;

            const response = await fetch(url, {
                body: JSON.stringify({
                    organization_id: activeOrganizationId || undefined,
                    category_id: formData.category_id || undefined,
                    amount: parseFloat(formData.amount),
                    member_ids: formData.member_ids,
                    title: formData.title,
                    type: modalType
                }),
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`
                },
                method: editId ? "PUT" : "POST"
            });

            const result = await response.json();
            if (result.status === "success") {
                setModalVisible(false);
                setEditId(null);
                setFormData({
                    category_id: "",
                    member_ids: [],
                    amount: "",
                    title: ""
                });
                fetchFinances();
            } else {
                alert(result.message || (result.errors ? result.errors.map((e: { message: string }) => e.message).join(", ") : localize("finance-toast-unknown-error")));
            }
        } catch (error) {
            console.error("Kayıt hatası:", error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = (id: string, title: string) => {
        let isDeleting = false;

        NCoreUIKitDialog.open({
            primaryButtonProps: {
                onPress: async ({
                    closeAnimation
                }) => {
                    if (isDeleting) return;

                    isDeleting = true;

                    try {
                        const token = await AsyncStorage.getItem("authToken");
                        const response = await fetch(`${process.env.API_URL}/finances/${id}`, {
                            body: JSON.stringify({
                                organization_id: activeOrganizationId
                            }),
                            headers: {
                                "Content-Type": "application/json",
                                Authorization: `Bearer ${token}`
                            },
                            method: "DELETE"
                        });

                        const result = await response.json();
                        if (result.status === "success") {
                            NCoreUIKitToast.open({
                                title: localize("finance-toast-deleted")
                            });
                            fetchFinances();
                        } else {
                            alert(result.message || (result.errors ? result.errors.map((e: { message: string }) => e.message).join(", ") : localize("finance-toast-unknown-error")));
                        }
                    } catch (error) {
                        console.error("Silme hatası:", error);
                    } finally {
                        isDeleting = false;

                        closeAnimation();
                    }
                },
                title: localize("finance-dialog-delete-primary"),
                type: "danger"
            },
            secondaryButtonProps: {
                title: localize("finance-dialog-delete-secondary"),
                onPress: ({
                    closeAnimation
                }) => closeAnimation(),
                type: "neutral"
            },
            content: localize("finance-dialog-delete-content", [
                title
            ]),
            title: localize("finance-dialog-delete-title"),
            variant: "yes-no",
            isVisible: true
        });
    };

    const getCategoryName = (id?: string) => {
        if (!id) return localize("finance-uncategorized");
        const cat = categories.find(c => c.id === id);
        return cat ? cat.name : localize("finance-unknown-category");
    };

    const mappedCategories = categories.map(c => ({
        ...c,
        __title: c.name,
        __key: c.id
    }));

    const mappedCategoriesForModal = categories
        .filter(c => c.type === modalType)
        .map(c => ({
            ...c,
            __title: c.name,
            __key: c.id
        }));

    return <PageContainer
        scrollViewStyle={isMobile ? [
            stylesheet.container,
            {
                padding: spaces.spacingMd
            }
        ] : undefined}
        isScrollable={true}
    >
        <View
            style={isMobile ? undefined : [
                stylesheet.container,
                {
                    padding: spaces.spacingXl
                }
            ]}
        >
            <View
                style={[
                    stylesheet.headerRow,
                    {
                        alignItems: isMobile ? "stretch" : "center",
                        flexDirection: isMobile ? "column" : "row",
                        gap: isMobile ? spaces.spacingMd : 0,
                        marginBottom: spaces.spacingXl
                    }
                ]}
            >
                <View
                    style={[
                        stylesheet.balanceContainer,
                        {
                            backgroundColor: colors.content.container.subtle,
                            borderRadius: radiuses.md,
                            padding: spaces.spacingLg
                        }
                    ]}
                >
                    <Text
                        variant="titleMediumSize"
                    >
                        {localize("finance-total-balance")}
                    </Text>
                    <Text
                        color={totalBalance >= 0 ? "success" : "danger"}
                        variant="titleLargeSize"
                    >
                        {totalBalance.toLocaleString("tr-TR", {
                            style: "currency",
                            currency: "TRY"
                        })}
                    </Text>
                </View>
                <View
                    style={[
                        stylesheet.actionsRow,
                        {
                            flexDirection: isMobile ? "column" : "row",
                            gap: spaces.spacingMd
                        }
                    ]}
                >
                    <Button
                        icon={({
                            color,
                            size
                        }) => {
                            return <RefreshCcwIcon
                                color={colors.content.icon[color as keyof typeof colors.content.icon]}
                                size={size}
                            />;
                        }}
                        onPress={async () => {
                            setIsRefreshing(true);
                            await fetchFinances();
                            setIsRefreshing(false);
                        }}
                        isLoading={isRefreshing}
                        spreadBehaviour="free"
                        variant="ghost"
                        type="neutral"
                    />
                    {(!user || user.role !== "root" || activeOrganizationId) && (
                        <>
                            <Button
                                spreadBehaviour={isMobile ? "stretch" : "free"}
                                title={localize("finance-add-income")}
                                onPress={() => {
                                    setModalType("income");
                                    setFormData(prev => ({
                                        ...prev,
                                        category_id: ""
                                    }));
                                    setModalVisible(true);
                                }}
                                type="success"
                            />
                            <Button
                                spreadBehaviour={isMobile ? "stretch" : "free"}
                                title={localize("finance-add-expense")}
                                onPress={() => {
                                    setModalType("expense");
                                    setFormData(prev => ({
                                        ...prev,
                                        category_id: ""
                                    }));
                                    setModalVisible(true);
                                }}
                                type="danger"
                            />
                        </>)}
                </View>
            </View>
            <View
                style={[
                    stylesheet.filterRow,
                    {
                        flexDirection: isMobile ? "column" : "row",
                        marginBottom: spaces.spacingXl,
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <View
                    style={stylesheet.filterCol}
                >
                    <TextInput
                        placeholder={localize("finance-search-placeholder")}
                        title={localize("finance-search-title")}
                        spreadBehaviour="stretch"
                        onChangeText={setSearch}
                        value={search}
                    />
                </View>
                <View>
                    <SelectBox
                        initialSelectedItems={mappedCategories.filter(c => c.__key === selectedCategoryId)}
                        onChange={(selected) => setSelectedCategoryId(selected[0]?.__key || "")}
                        placeholder={localize("finance-category-placeholder")}
                        key={`category-filter-${mappedCategories.length}`}
                        spreadBehaviour={isMobile ? "stretch" : "free"}
                        title={localize("finance-category-filter")}
                        titleExtractor={(item) => item.__title}
                        keyExtractor={(item) => item.__key}
                        data={mappedCategories}
                    />
                </View>
                {
                    selectedCategoryId ?
                        <Button
                            spreadBehaviour={isMobile ? "stretch" : "free"}
                            onPress={() => setSelectedCategoryId("")}
                            title={localize("finance-clear")}
                            variant="ghost"
                            color="danger"
                        />
                    :
                        null
                }
            </View>
            <View
                style={[
                    stylesheet.dateFilterRow,
                    {
                        flexDirection: isMobile ? "column" : "row",
                        marginBottom: spaces.spacingXl,
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <View
                    style={stylesheet.dateFilterCol}
                >
                    <DateTimePicker
                        onChange={({
                            dateRange
                        }) => {
                            if (dateRange?.start) setStartDate(new Date(dateRange.start).toISOString());
                            else setStartDate("");

                            if (dateRange?.end) setEndDate(new Date(dateRange.end).toISOString());
                            else setEndDate("");
                        }}
                        placeholder={localize("finance-date-filter-placeholder")}
                        title={localize("finance-date-filter-title")}
                        spreadBehaviour="stretch"
                        variant="range"
                    />
                </View>
            </View>
            <View
                style={[
                    stylesheet.listHeaderRow,
                    {
                        marginBottom: spaces.spacingMd,
                        paddingRight: spaces.spacingMd
                    }
                ]}
            >
                <Text
                    variant="titleMediumSize"
                >
                    {localize("finance-filtered-total", [
                        filteredTotal.toLocaleString("tr-TR", {
                            style: "currency",
                            currency: "TRY"
                        })
                    ])}
                </Text>
            </View>
            <View
                style={{
                    flex: isMobile ? undefined : 1,
                    gap: spaces.spacingMd
                }}
            >
                <ScrollView
                    contentContainerStyle={[
                        finances.length === 0 ? stylesheet.emptyListContent : stylesheet.listContent,
                        {
                            paddingBottom: spaces.spacingXl,
                            gap: spaces.spacingMd
                        }
                    ]}
                    showsVerticalScrollIndicator={false}
                >
                    {
                        finances.length === 0 ?
                            <StateCard
                                subTitle={localize("finance-empty-subtitle")}
                                title={localize("finance-empty-title")}
                                icon={({
                                    color,
                                    size
                                }) => <SearchIcon
                                    color={colors.content.icon[color]}
                                    size={size}
                                />}
                            />
                        :
                            (
                            finances.map((item) => {
                                return <TouchableOpacity
                                    style={[
                                        stylesheet.itemContainer,
                                        {
                                            backgroundColor: colors.content.container.mid,
                                            alignItems: isMobile ? "stretch" : "center",
                                            flexDirection: isMobile ? "column" : "row",
                                            borderRadius: radiuses.md,
                                            padding: spaces.spacingMd,
                                            gap: spaces.spacingMd
                                        }
                                    ]}
                                    activeOpacity={0.7}
                                    key={item.id}
                                >
                                    <View
                                        style={[
                                            stylesheet.itemContent,
                                            {
                                                gap: spaces.spacingMd
                                            }
                                        ]}
                                    >
                                        <View
                                            style={[
                                                stylesheet.itemIcon,
                                                {
                                                    backgroundColor: colors.content.container.default,
                                                    borderRadius: radiuses.full
                                                }
                                            ]}
                                        >
                                            <BanknoteIcon
                                                color={colors.content.icon.emphasized}
                                                size={24}
                                            />
                                        </View>
                                        <View
                                            style={[
                                                stylesheet.itemInfo,
                                                {
                                                    gap: spaces.spacingXs
                                                }
                                            ]}
                                        >
                                            <Text
                                                variant="titleMediumSize"
                                            >
                                                {item.title}
                                            </Text>
                                            <Text
                                                variant="bodyMediumSize"
                                                color="mid"
                                            >
                                                {new Date(item.createdAt).toLocaleDateString("tr-TR")} • {getCategoryName(item.category_id)}
                                            </Text>
                                            {item.member_ids && item.member_ids.length > 0 && (
                                                <Text
                                                    variant="bodySmallSize"
                                                    color="mid"
                                                >
                                                    {localize("finance-members", [
                                                        item.member_ids.map(mId => members.find(m => m.id === mId)?.name_surname).filter(Boolean).join(", ")
                                                    ])}
                                                </Text>)}
                                        </View>
                                    </View>
                                    <View
                                        style={[
                                            stylesheet.itemActions,
                                            {
                                                alignItems: isMobile ? "stretch" : "center",
                                                flexDirection: isMobile ? "column" : "row",
                                                gap: spaces.spacingMd
                                            }
                                        ]}
                                    >
                                        <Text
                                            color={item.type === "income" ? "success" : "danger"}
                                            variant="titleMediumSize"
                                        >
                                            {item.type === "income" ? "+" : "-"} {item.amount.toLocaleString("tr-TR", {
                                                style: "currency",
                                                currency: "TRY"
                                            })}
                                        </Text>
                                        <Button
                                            icon={({
                                                color,
                                                size
                                            }) => {
                                                return <PencilIcon
                                                    color={colors.content.icon[color as keyof typeof colors.content.icon]}
                                                    size={size}
                                                />;
                                            }}
                                            spreadBehaviour={isMobile ? "stretch" : "free"}
                                            onPress={() => {
                                                setEditId(item.id);
                                                setModalType(item.type);
                                                setFormData({
                                                    category_id: item.category_id || "",
                                                    member_ids: item.member_ids || [],
                                                    amount: item.amount.toString(),
                                                    title: item.title
                                                });
                                                setModalVisible(true);
                                            }}
                                            title={localize("finance-list-edit")}
                                            type="neutral"
                                        />
                                        <Button
                                            icon={({
                                                color,
                                                size
                                            }) => {
                                                return <TrashIcon
                                                    color={colors.content.icon[color as keyof typeof colors.content.icon]}
                                                    size={size}
                                                />;
                                            }}
                                            onPress={() => handleDelete(item.id, item.title)}
                                            spreadBehaviour={isMobile ? "stretch" : "free"}
                                            title={localize("finance-list-delete")}
                                            type="danger"
                                        />
                                    </View>
                                </TouchableOpacity>;
                            })
                    )
                    }
                </ScrollView>
                {
                    totalPages > 1 && (
                        <View
                            style={[
                                stylesheet.paginationContainer,
                                {
                                    paddingTop: spaces.spacingMd,
                                    gap: spaces.spacingSm,
                                    flexWrap: "wrap"
                                }
                            ]}
                        >
                            <Button
                                icon={({
                                    color,
                                    size
                                }) => <ChevronLeftIcon
                                    color={colors.content.icon[color as keyof typeof colors.content.icon]}
                                    size={size}
                                />
                                }
                                onPress={() => setPage(p => Math.max(1, p - 1))}
                                title={localize("finance-pagination-prev")}
                                isDisabled={page === 1}
                                color="secondary"
                            />
                            <Text
                                variant="bodyMediumSize"
                            >
                                {localize("finance-pagination-info", [
                                    page.toString(),
                                    totalPages.toString()
                                ])}
                            </Text>
                            <Button
                                icon={({
                                    color,
                                    size
                                }) => <ChevronRightIcon
                                    color={colors.content.icon[color as keyof typeof colors.content.icon]}
                                    size={size}
                                />
                                }
                                onPress={() => setPage(p => Math.min(totalPages, p + 1))}
                                title={localize("finance-pagination-next")}
                                isDisabled={page === totalPages}
                                iconDirection="right"
                                color="secondary"
                            />
                            <View
                                style={[
                                    stylesheet.jumpContainer,
                                    {
                                        marginLeft: isMobile ? 0 : spaces.spacingXl,
                                        gap: spaces.spacingMd
                                    }
                                ]}
                            >
                                <View
                                    style={stylesheet.jumpInputContainer}
                                >
                                    <NativeTextInput
                                        onChangeText={(text) => setJumpPage(text.replace(/[^0-9]/g, ""))}
                                        style={[
                                            stylesheet.jumpInput,
                                            {
                                                borderColor: colors.content.border.subtle,
                                                color: colors.content.text.high
                                            }
                                        ]}
                                        placeholder={localize("finance-jump-placeholder")}
                                        placeholderTextColor={colors.content.text.low}
                                        keyboardType="numeric"
                                        value={jumpPage}
                                    />
                                </View>
                                <Button
                                    title={localize("finance-jump-button")}
                                    onPress={() => {
                                        const p = parseInt(jumpPage, 10);
                                        if (p >= 1 && p <= totalPages) {
                                            setPage(p);
                                        }
                                        setJumpPage("");
                                    }}
                                    isDisabled={!jumpPage}
                                    color="secondary"
                                />
                            </View>
                        </View>)
                }
            </View>
            <Modal
                onOverlayPress={({
                    closeAnimation
                }) => closeAnimation(() => {
                    setModalVisible(false);
                    setEditId(null);
                    setFormData({
                        category_id: "",
                        member_ids: [],
                        amount: "",
                        title: ""
                    });
                })}
                onClosed={() => {
                    setModalVisible(false);
                    setEditId(null);
                    setFormData({
                        category_id: "",
                        member_ids: [],
                        amount: "",
                        title: ""
                    });
                }}
                portalName="modal-system"
                isActive={modalVisible}
                id="finance-modal"
            >
                <View
                    style={[
                        stylesheet.modalContent,
                        {
                            backgroundColor: colors.content.container.default,
                            width: isMobile ? "100%" : 500,
                            borderRadius: radiuses.md,
                            padding: spaces.spacingXl
                        }
                    ]}
                >
                    <Text
                        variant="titleLargeSize"
                    >
                        {editId ? (modalType === "income" ? localize("finance-modal-edit-income") : localize("finance-modal-edit-expense")) : (modalType === "income" ? localize("finance-modal-add-income") : localize("finance-modal-add-expense"))}
                    </Text>
                    <ScrollView
                        showsVerticalScrollIndicator={false}
                        style={{
                            flexShrink: 1
                        }}
                    >
                        <View
                            style={[
                                stylesheet.formContainer,
                                {
                                    marginTop: spaces.spacingLg,
                                    gap: spaces.spacingLg
                                }
                            ]}
                        >
                            <TextInput
                                placeholder={localize("finance-modal-title-placeholder")}
                                onChangeText={(text: string) => setFormData(prev => ({
                                    ...prev,
                                    title: text
                                }))}
                                title={localize("finance-modal-title-title")}
                                defaultValue={formData.title}
                                spreadBehaviour="stretch"
                            />
                            <TextInput
                                placeholder={localize("finance-modal-amount-placeholder")}
                                onChangeText={(text: string) => {
                                    const numeric = text.replace(/[^0-9]/g, "");
                                    setFormData(prev => ({
                                        ...prev,
                                        amount: numeric
                                    }));
                                }}
                                title={localize("finance-modal-amount-title")}
                                defaultValue={formData.amount}
                                spreadBehaviour="stretch"
                                keyboardType="numeric"
                            />
                            <View>
                                <SelectBox
                                    initialSelectedItems={mappedCategoriesForModal.filter(c => c.__key === formData.category_id)}
                                    key={`selectbox-${modalType}-${modalVisible ? "visible" : "hidden"}`}
                                    placeholder={localize("finance-category-placeholder")}
                                    title={localize("finance-modal-category-label")}
                                    spreadBehaviour={isMobile ? "stretch" : "free"}
                                    onChange={(selected) => setFormData(prev => ({
                                        ...prev,
                                        category_id: selected[0]?.__key || ""
                                    }))}
                                    titleExtractor={(item) => item.__title}
                                    keyExtractor={(item) => item.__key}
                                    data={mappedCategoriesForModal}
                                    selectSheetProps={{
                                        portalName: "modal-system"
                                    }}
                                />
                            </View>
                            <View>
                                <Text
                                    style={[
                                        stylesheet.selectBoxLabel,
                                        {
                                            marginBottom: spaces.spacingSm,
                                            marginTop: spaces.spacingMd
                                        }
                                    ]}
                                    variant="bodyMediumSize"
                                >
                                    {localize("finance-modal-members-label")}
                                </Text>
                                <SelectBox
                                    key={`selectbox-members-${modalVisible ? "visible" : "hidden"}`}
                                    placeholder={localize("finance-modal-members-placeholder")}
                                    initialSelectedItems={members.map(m => ({
                                        __title: m.name_surname,
                                        __key: m.id
                                    })).filter(m => formData.member_ids.includes(m.__key))}
                                    spreadBehaviour={isMobile ? "stretch" : "free"}
                                    onChange={(selected) => setFormData(prev => ({
                                        ...prev,
                                        member_ids: selected.map(s => s.__key)
                                    }))}
                                    titleExtractor={(item) => item.__title}
                                    keyExtractor={(item) => item.__key}
                                    selectSheetProps={{
                                        portalName: "modal-system"
                                    }}
                                    data={members.map(m => ({
                                        __title: m.name_surname,
                                        __key: m.id
                                    }))}
                                    isMultipleSelect={true}
                                />
                            </View>
                        </View>
                    </ScrollView>
                    <View
                        style={[
                            stylesheet.modalActions,
                            {
                                flexDirection: isMobile ? "column-reverse" : "row",
                                marginTop: spaces.spacingXl,
                                gap: spaces.spacingMd
                            }
                        ]}
                    >
                        <Button
                            title={localize("finance-modal-cancel")}
                            onPress={() => {
                                setModalVisible(false);
                                setEditId(null);
                                setFormData({
                                    category_id: "",
                                    member_ids: [],
                                    amount: "",
                                    title: ""
                                });
                            }}
                            spreadBehaviour="stretch"
                            type="neutral"
                        />
                        <Button
                            title={localize("finance-modal-save")}
                            spreadBehaviour="stretch"
                            isLoading={isSubmitting}
                            onPress={handleSave}
                            type="primary"
                        />
                    </View>
                </View>
            </Modal>
        </View>
    </PageContainer>;
};
export default Finance;
